import React, { useState, useRef, useEffect } from 'react'
import { X, FileText, CheckCircle, PenTool, RotateCcw, Type } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { useAuth } from '../../contexts/AuthContext'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { eSigning } from '../../services/eSigning'
import toast from 'react-hot-toast'

interface ExchangeAgreementModalProps {
  isOpen: boolean
  onClose: () => void
  onSignComplete: () => void
  userData: {
    firstName: string
    lastName: string
    email: string
    address: {
      street: string
      city: string
      state: string
      zipCode: string
    }
    ssn?: string
  }
}

export const ExchangeAgreementModal: React.FC<ExchangeAgreementModalProps> = ({
  isOpen,
  onClose,
  onSignComplete,
  userData
}) => {
  const { user } = useAuth()
  useBodyScrollLock(isOpen)
  const [isSigning, setIsSigning] = useState(false)
  
  // Check if already signed
  const isSignedCheck = localStorage.getItem('exchange_agreement_signed') === 'true'
  const signedDataStr = localStorage.getItem('exchange_agreement_data')
  let signedDataParsed: any = null
  if (signedDataStr) {
    try {
      signedDataParsed = JSON.parse(signedDataStr)
    } catch (e) {}
  }
  
  const [isSigned, setIsSigned] = useState(isSignedCheck)
  const [signature, setSignature] = useState(() => {
    // Load signature from localStorage if viewing signed document
    if (isSignedCheck && signedDataParsed) {
      return signedDataParsed.signatureMode === 'type' ? signedDataParsed.signature : ''
    }
    return ''
  })
  const [signatureImage, setSignatureImage] = useState<string | null>(() => {
    // Load signature image from localStorage if viewing signed document
    if (isSignedCheck && signedDataParsed) {
      return signedDataParsed.signatureMode === 'draw' ? signedDataParsed.signature : null
    }
    return null
  })
  const [showSignaturePad, setShowSignaturePad] = useState(false)
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw')
  const [useSavedSignature, setUseSavedSignature] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const signatureInputRef = useRef<HTMLInputElement>(null)
  
  // Check for saved signature
  const savedSignatureStr = localStorage.getItem('user_saved_signature')
  const savedSignature = savedSignatureStr ? (() => {
    try {
      return JSON.parse(savedSignatureStr)
    } catch (e) {
      return null
    }
  })() : null

  // Auto-complete if already signed
  React.useEffect(() => {
    if (isSigned && isOpen) {
      // Small delay to show the signed state
      const timer = setTimeout(() => {
        onSignComplete()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isSigned, isOpen, onSignComplete])

  if (!isOpen) return null

  // Initialize canvas for drawing signature
  useEffect(() => {
    if (canvasRef.current && signatureMode === 'draw') {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.strokeStyle = '#1e293b'
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
      }
    }
  }, [signatureMode])

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    setIsDrawing(true)
    const rect = canvas.getBoundingClientRect()
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const stopDrawing = () => {
    if (!canvasRef.current) return
    setIsDrawing(false)
    // Convert canvas to image
    const canvas = canvasRef.current
    const imageData = canvas.toDataURL('image/png')
    setSignatureImage(imageData)
  }

  const clearSignature = () => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    setSignatureImage(null)
    setSignature('')
  }

  // Auto-show signature pad when modal opens and document is not signed
  useEffect(() => {
    if (isOpen && !isSigned) {
      setShowSignaturePad(true)
      // If there's a saved signature, offer to use it
      if (savedSignature) {
        setUseSavedSignature(true)
        if (savedSignature.signatureMode === 'draw') {
          setSignatureImage(savedSignature.signature)
          setSignatureMode('draw')
        } else {
          setSignature(savedSignature.signature)
          setSignatureMode('type')
        }
      }
    }
  }, [isOpen, isSigned, savedSignature])

  const handleUseSavedSignature = () => {
    if (savedSignature) {
      if (savedSignature.signatureMode === 'draw') {
        setSignatureImage(savedSignature.signature)
        setSignatureMode('draw')
      } else {
        setSignature(savedSignature.signature)
        setSignatureMode('type')
      }
      setUseSavedSignature(true)
    }
  }

  const handleCreateNewSignature = () => {
    setUseSavedSignature(false)
    clearSignature()
  }

  const handleSignatureComplete = () => {
    // Use saved signature if selected, otherwise use current signature
    let signatureValue: string
    let currentMode: 'draw' | 'type'
    
    if (useSavedSignature && savedSignature) {
      signatureValue = savedSignature.signature
      currentMode = savedSignature.signatureMode
    } else {
      const hasSignature = signatureMode === 'draw' ? signatureImage : signature.trim()
      if (!hasSignature) return
      signatureValue = signatureMode === 'draw' ? signatureImage! : signature
      currentMode = signatureMode
    }

    setIsSigning(true)
    // Simulate signing process
    setTimeout(async () => {
      setIsSigned(true)
      setIsSigning(false)
      setShowSignaturePad(false)
      
      // Only update saved signature if creating a new one (not using saved)
      if (!useSavedSignature) {
        // Store signature globally for reuse on other documents
        const newSavedSignature = {
          signature: signatureValue,
          signatureMode: currentMode,
          createdAt: new Date().toISOString(),
          createdBy: `${userData.firstName} ${userData.lastName}`
        }
        localStorage.setItem('user_saved_signature', JSON.stringify(newSavedSignature))
      }
      
      // Store signed status and document data
      const signedData = {
        signed: true,
        signedDate: new Date().toISOString(),
        signedBy: `${userData.firstName} ${userData.lastName}`,
        signature: signatureValue,
        signatureMode: currentMode,
        userData
      }
      localStorage.setItem('exchange_agreement_signed', 'true')
      localStorage.setItem('exchange_agreement_signed_date', signedData.signedDate)
      localStorage.setItem('exchange_agreement_signed_by', signedData.signedBy)
      localStorage.setItem('exchange_agreement_data', JSON.stringify(signedData))
      
      // Add to e-signing service
      const docId = `exchange_agreement_${Date.now()}`
      eSigning.addDocument({
        id: docId,
        name: 'Exchange Agreement',
        type: 'exchange_agreement',
        signedBy: signedData.signedBy
      })
      await eSigning.signDocument(docId, signedData.signedBy)
      eSigning.completeDocument(docId)
      toast.success('Document signed successfully!')
      
      // Also store in documents storage
      const docsKey = 'exchange_docs_v1'
      try {
        const existingDocs = localStorage.getItem(docsKey)
        const docs = existingDocs ? JSON.parse(existingDocs) : {}
        docs['Exchange Agreement'] = {
          name: 'Exchange_Agreement_Signed.pdf',
          size: 1048576,
          status: 'verified',
          signed: true,
          signedDate: signedData.signedDate,
          signedBy: signedData.signedBy
        }
        localStorage.setItem(docsKey, JSON.stringify(docs))
      } catch (e) {
        console.error('Failed to save document:', e)
      }
    }, 1500)
  }

  const handleFinalize = () => {
    onSignComplete()
    onClose()
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  return (
    <div className="modal-overlay modal-overlay-nested">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-[#69adba] to-[#5a9aa6] flex items-center justify-center flex-shrink-0 shadow-sm">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Exchange Agreement
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Please review and sign to continue
              </p>
            </div>
          </div>
          {!isSigned && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </button>
          )}
        </div>

        {/* Document Content */}
        <div className="flex-1 overflow-y-scroll pr-2 min-h-0 p-4 sm:p-6" style={{ maxHeight: '100%' }}>
          <Card className="p-6 sm:p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
            <div className="space-y-6 text-slate-900 dark:text-slate-100">
              {/* Header */}
              <div className="text-center border-b border-slate-200 dark:border-slate-700 pb-6">
                <h1 className="text-2xl font-bold mb-2">1031 EXCHANGE AGREEMENT</h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Qualified Intermediary Services Agreement
                </p>
              </div>

              {/* Parties */}
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">PARTIES:</h3>
                  <p className="text-sm leading-relaxed">
                    This Exchange Agreement ("Agreement") is entered into on {formatDate(new Date())} between:
                  </p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="font-semibold mb-2">Exchanger:</p>
                  <p className="text-sm">
                    {userData.firstName} {userData.lastName}<br />
                    {userData.address.street}<br />
                    {userData.address.city}, {userData.address.state} {userData.address.zipCode}<br />
                    Email: {userData.email}
                  </p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="font-semibold mb-2">Qualified Intermediary:</p>
                  <p className="text-sm">
                    Open1031, LLC<br />
                    Registered Investment Advisor & Qualified Intermediary<br />
                    Email: support@open1031.com
                  </p>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-4">
                <h3 className="font-semibold">TERMS AND CONDITIONS:</h3>
                
                <div className="space-y-3 text-sm leading-relaxed">
                  <p>
                    <strong>1. Exchange Purpose:</strong> The Exchanger intends to complete a like-kind exchange 
                    under Section 1031 of the Internal Revenue Code, involving the relinquished property and 
                    replacement property(ies) to be identified.
                  </p>

                  <p>
                    <strong>2. Qualified Intermediary Services:</strong> Open1031 agrees to serve as the Qualified 
                    Intermediary for this exchange, holding exchange proceeds in a segregated account and facilitating 
                    the exchange transaction in accordance with IRS regulations.
                  </p>

                  <p>
                    <strong>3. Exchange Proceeds:</strong> All proceeds from the sale of the relinquished property 
                    shall be held by Open1031 in a segregated escrow account. The Exchanger acknowledges that they 
                    will not have actual or constructive receipt of these funds during the exchange period.
                  </p>

                  <p>
                    <strong>4. Identification Period:</strong> The Exchanger agrees to identify replacement property(ies) 
                    within 45 days of the closing of the relinquished property, in accordance with Section 1031 requirements.
                  </p>

                  <p>
                    <strong>5. Exchange Period:</strong> The Exchanger agrees to acquire the replacement property(ies) 
                    within 180 days of the closing of the relinquished property, or by the due date (including extensions) 
                    of the Exchanger's tax return for the year in which the relinquished property was transferred, 
                    whichever is earlier.
                  </p>

                  <p>
                    <strong>6. Fees:</strong> The Exchanger agrees to pay Open1031's standard fees for Qualified 
                    Intermediary services as outlined in the fee schedule provided separately.
                  </p>

                  <p>
                    <strong>7. Representations:</strong> The Exchanger represents that all information provided is 
                    accurate and complete, and that they have the authority to enter into this Agreement.
                  </p>

                  <p>
                    <strong>8. Compliance:</strong> Both parties agree to comply with all applicable federal, state, 
                    and local laws and regulations governing 1031 exchanges.
                  </p>
                </div>
              </div>

              {/* Signature Section */}
              {isSigned ? (
                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center space-x-2 text-green-600 dark:text-green-400 mb-4">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold">Document Signed</span>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                          <strong>Signed by:</strong> {userData.firstName} {userData.lastName}
                        </p>
                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                          <strong>Date:</strong> {formatDate(new Date())}
                        </p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          <strong>IP Address:</strong> {window.location.hostname}
                        </p>
                      </div>
                      {signatureImage && (
                        <div className="flex-shrink-0">
                          <img src={signatureImage} alt="Signature" className="h-16 w-auto border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 p-2" />
                        </div>
                      )}
                      {signature && !signatureImage && (
                        <div className="flex-shrink-0">
                          <div className="h-16 px-4 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 flex items-center">
                            <p className="text-xl font-signature text-slate-900 dark:text-slate-100">{signature}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <Button
                      onClick={handleFinalize}
                      className="bg-gradient-to-r from-[#69adba] to-[#5a9aa6] text-white"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Continue
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                  <div className="mb-4">
                    <h3 className="font-semibold mb-2 text-slate-900 dark:text-slate-100">Sign Document</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Please sign below to complete the agreement
                    </p>
                  </div>

                  {/* Saved Signature Option */}
                  {savedSignature && !useSavedSignature && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {savedSignature.signatureMode === 'draw' && savedSignature.signature ? (
                            <img src={savedSignature.signature} alt="Saved signature" className="h-12 w-auto border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 p-1" />
                          ) : savedSignature.signature ? (
                            <div className="h-12 px-3 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 flex items-center">
                              <p className="text-lg font-signature text-slate-900 dark:text-slate-100">{savedSignature.signature}</p>
                            </div>
                          ) : null}
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Use your saved signature?</p>
                            <p className="text-xs text-slate-600 dark:text-slate-400">
                              Created on {new Date(savedSignature.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUseSavedSignature}
                            className="text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600"
                          >
                            Use Saved
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCreateNewSignature}
                          >
                            Create New
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Signature Mode Toggle */}
                  <div className="flex gap-2 mb-4">
                    {!useSavedSignature && (
                      <>
                        <button
                          onClick={() => {
                            setSignatureMode('draw')
                            clearSignature()
                          }}
                          className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                            signatureMode === 'draw'
                              ? 'border-[#69adba] bg-[#69adba]/10 dark:bg-[#69adba]/20'
                              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                        >
                          <PenTool className={`h-4 w-4 mx-auto mb-1 ${signatureMode === 'draw' ? 'text-[#69adba]' : 'text-slate-600 dark:text-slate-400'}`} />
                          <span className={`text-xs font-medium ${signatureMode === 'draw' ? 'text-[#69adba]' : 'text-slate-600 dark:text-slate-400'}`}>Draw</span>
                        </button>
                        <button
                          onClick={() => {
                            setSignatureMode('type')
                            clearSignature()
                          }}
                          className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                            signatureMode === 'type'
                              ? 'border-[#69adba] bg-[#69adba]/10 dark:bg-[#69adba]/20'
                              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                        >
                          <Type className={`h-4 w-4 mx-auto mb-1 ${signatureMode === 'type' ? 'text-[#69adba]' : 'text-slate-600 dark:text-slate-400'}`} />
                          <span className={`text-xs font-medium ${signatureMode === 'type' ? 'text-[#69adba]' : 'text-slate-600 dark:text-slate-400'}`}>Type</span>
                        </button>
                      </>
                    )}
                    {useSavedSignature && (
                      <div className="w-full p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Using saved signature</p>
                            <p className="text-xs text-slate-600 dark:text-slate-400">
                              Created on {savedSignature ? new Date(savedSignature.createdAt).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCreateNewSignature}
                        >
                          Change
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Signature Pad */}
                  {useSavedSignature && savedSignature ? (
                    <div className="bg-white dark:bg-slate-900 rounded-lg border-2 border-green-300 dark:border-green-600 mb-4 p-6">
                      <div className="flex items-center justify-center min-h-[120px]">
                        {savedSignature.signatureMode === 'draw' && savedSignature.signature ? (
                          <img src={savedSignature.signature} alt="Saved signature" className="max-h-32 w-auto border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 p-2" />
                        ) : savedSignature.signature ? (
                          <div className="px-6 py-4 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800">
                            <p className="text-3xl font-signature text-slate-900 dark:text-slate-100 text-center">{savedSignature.signature}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-slate-900 rounded-lg border-2 border-slate-300 dark:border-slate-600 mb-4 overflow-hidden">
                      {signatureMode === 'draw' ? (
                      <div className="relative">
                        <canvas
                          ref={canvasRef}
                          width={600}
                          height={200}
                          className="w-full h-48 cursor-crosshair touch-none"
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                        />
                        {(signatureImage || isDrawing) && (
                          <button
                            onClick={clearSignature}
                            className="absolute top-2 right-2 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-md hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600"
                            title="Clear signature"
                          >
                            <RotateCcw className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                          </button>
                        )}
                        {!signatureImage && !isDrawing && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                              <PenTool className="h-8 w-8 text-slate-400 dark:text-slate-500 mx-auto mb-2" />
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                Draw your signature here
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-6">
                        <input
                          ref={signatureInputRef}
                          type="text"
                          value={signature}
                          onChange={(e) => setSignature(e.target.value)}
                          placeholder={`${userData.firstName} ${userData.lastName}`}
                          className="w-full px-4 py-3 text-2xl font-signature text-center border-0 bg-transparent text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-0 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          autoFocus
                        />
                        <div className="mt-2 text-center">
                          <button
                            onClick={clearSignature}
                            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1 mx-auto"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowSignaturePad(false)
                        clearSignature()
                        onClose()
                      }}
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSignatureComplete}
                      disabled={useSavedSignature ? false : (signatureMode === 'draw' ? !signatureImage : !signature.trim())}
                      className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] text-white disabled:opacity-50"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {isSigning ? 'Signing...' : 'Sign Document'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

